import { createContext, useContext, useEffect, useReducer, useState } from 'react'
import { useAuth } from '.'
import { reactGAEvent } from '../utils'

const FormContext = createContext(null)

const initialFieldValues = {
    cmpName: '',
    name: '',
    email: '',
    phoneNumber: '',
    jobType: '',
    tags: [],
    otherJobType: '',
    siteAddress: '',
    numOfRequirements: {
        supervisor: 0,
        helper: 0,
        technician: 0,
    },
    startDate: new Date().toISOString().split('T')[0],
    durationType: 'less than 7 days',
    state: '',
    city: '',
    shiftTime: '',
}

const formReducer = (state, action) => {
    const { type, payload } = action

    switch (type) {
        case 'JOB_TYPE':
            return { ...state, jobType: payload.value }

        case 'SHIFT_TIME':
            return { ...state, shiftTime: payload.value }

        case 'RESET_TAGS':
            return { ...state, tags: [] }

        case 'RESET_JOB_TYPE':
            return { ...state, jobType: '' }

        case 'TAGS':
            let updatedJobList
            if (payload.type === 'REMOVE') {
                console.log('remove called')
                updatedJobList = state.tags.filter((job) => job !== payload.value)
                console.log(updatedJobList)
            }
            if (payload.type === 'ADD') {
                updatedJobList = [...state.tags, payload.value]
            }
            return { ...state, tags: updatedJobList }

        case 'NUM_OF_REQUIREMENTS':
            let updatedRequirements
            if (payload.type === 'supervisor') {
                updatedRequirements = {
                    ...state.numOfRequirements,
                    supervisor: payload.value,
                }
            }
            if (payload.type === 'helper') {
                updatedRequirements = {
                    ...state.numOfRequirements,
                    helper: payload.value,
                }
            }

            if (payload.type === 'technician') {
                updatedRequirements = {
                    ...state.numOfRequirements,
                    technician: payload.value,
                }
            }
            return { ...state, numOfRequirements: updatedRequirements }

        case 'OTHER_JOB_TYPE':
            return { ...state, otherJobType: payload.value }

        case 'NAME':
            return { ...state, name: payload.value }

        case 'CMP_NAME':
            return { ...state, cmpName: payload.value }

        case 'EMAIL':
            return { ...state, email: payload.value }

        case 'PHONE_NUMBER':
            return { ...state, phoneNumber: payload.value }

        case 'SUP_COUNT':
            return { ...state, supCount: payload.value }

        case 'HELP_COUNT':
            return { ...state, helpCount: payload.value }

        case 'TECH_COUNT':
            return { ...state, techCount: payload.value }

        case 'START_DATE':
            return { ...state, startDate: payload.value }

        case 'DURATION_TYPE':
            return { ...state, durationType: payload.value }

        case 'SITE_ADDRESS':
            return { ...state, siteAddress: payload.value }

        case 'STATE':
            return { ...state, state: payload.value }

        case 'CITY':
            return { ...state, city: payload.value }

        case 'RESET':
            return initialFieldValues

        default:
            throw new Error('Invalid field type')
    }
}

export function FormProvider({ children }) {
    const [formState, dispatch] = useReducer(formReducer, initialFieldValues)
    console.log(formState)
    const { isUserLoggedIn, user } = useAuth()
    const [activeStep, setActiveStep] = useState(1)
    const [currentJobType, setCurrentJobType] = useState(formState.jobType)

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1)
    }

    const handleBack = () => {
        reactGAEvent('Button', 'Go back to part one of booking form button clicked')
        setActiveStep((prevActiveStep) => prevActiveStep - 1)
    }

    const handleReset = () => {
        setActiveStep(0)
    }

    const [error, setError] = useState({})

    const onSubmitPartOne = () => {
        reactGAEvent('Button', 'Booking form part one submit button clicked')
        const errorObj = {}
        setError((prevError) => ({ ...prevError, error: {} }))

        const technician = parseInt(formState.numOfRequirements.technician)
        const helper = parseInt(formState.numOfRequirements.helper)
        const supervisor = parseInt(formState.numOfRequirements.supervisor)

        if (technician < 1 && helper < 1 && supervisor < 1) {
            errorObj['numOfRequirements'] = {
                error: 'Please fill the requirements',
            }
        }

        if (!technician && !helper && !supervisor) {
            errorObj['numOfRequirements'] = {
                error: 'Please fill the requirements',
            }
        }
        //Uncomment to make tags a mandatory field
        // if (formState["tags"].length === 0) {
        //   if (formState.otherJobType.length === 0) {
        //     errorObj["otherJobType"] = {
        //       error: "Please fill the requirements",
        //     };
        //   }
        // }

        ;['state', 'city', 'startDate', 'siteAddress', 'jobType', 'shiftTime'].forEach((field) => {
            if (formState[field].length === 0) {
                errorObj[field] = { error: 'Please fill this field' }
            }
        })
        setError({ ...errorObj })
        if (Object.keys(errorObj).length === 0) {
            handleNext()
        }
    }

    useEffect(() => {
        if (!isUserLoggedIn) {
            dispatch({
                type: 'PHONE_NUMBER',
                payload: { type: 'phoneNumber', value: '' },
            })
        }
        if (isUserLoggedIn) {
            dispatch({
                type: 'PHONE_NUMBER',
                payload: { type: 'phoneNumber', value: user.phoneNumber.slice(3) },
            })
        }
    }, [isUserLoggedIn, user.phoneNumber])

    const handleRequirements = (name, value) => {
        if (parseInt(value) < 0) {
            return dispatch({
                type: 'NUM_OF_REQUIREMENTS',
                payload: { type: name, value: '0' },
            })
        }

        dispatch({
            type: 'NUM_OF_REQUIREMENTS',
            payload: { type: name, value },
        })
    }

    return (
        <FormContext.Provider
            value={{
                formState,
                activeStep,
                error,
                currentJobType,
                setCurrentJobType,
                dispatch,
                setError,
                onSubmitPartOne,
                handleNext,
                handleBack,
                handleReset,
                handleRequirements,
            }}
        >
            {children}
        </FormContext.Provider>
    )
}

export function useForm() {
    return useContext(FormContext)
}
